import { create } from 'zustand';

import {
  getPhotoAnalysis,
  startPhotoAnalysis,
  type PhotoAnalysisResult,
  type StartPhotoAnalysisInput,
} from '../../../api/productV2Api';

const POLL_MS = 2500;
/** The generator reads a dozen photos in well under a minute; past this, something is stuck. */
const GIVE_UP_MS = 4 * 60 * 1000;

export interface PhotoRun {
  phase: 'running' | 'result' | 'error';
  /** reading: the upload and the vision call; cutting: the generator's computer vision. */
  step: 'reading' | 'cutting';
  result?: PhotoAnalysisResult;
  /** A server message, or 'timeout' / 'failed' for the dialog to translate. */
  error?: string;
}

interface BuildFromPhotosState {
  /** One run per variant (keyed by its uuid). */
  runs: Record<string, PhotoRun>;
  start: (key: string, input: StartPhotoAnalysisInput) => Promise<void>;
  reset: (key: string) => void;
  /**
   * Photos offered to a variant's dialog before it is opened (the ones "Add
   * product with AI" found on the page), by variant uuid. The variant's row
   * opens itself and its dialog on them, ticked; the vendor still starts it.
   */
  offers: Record<string, string[]>;
  offer: (key: string, urls: string[]) => void;
  takeOffer: (key: string) => string[] | undefined;
}

/**
 * "Build from photos" runs, outside the dialog.
 *
 * The dialog tells the vendor they can close it while the photos are read, so
 * the run cannot live in the dialog: closing it (or collapsing the variant's
 * row, which unmounts it) would drop the result on the floor, and the variant's
 * button would have no way to show that something is still working.
 */
export const useBuildFromPhotos = create<BuildFromPhotosState>((set, get) => {
  const put = (key: string, run: PhotoRun | undefined) =>
    set((s) => {
      const runs = { ...s.runs };
      if (run) runs[key] = run; else delete runs[key];
      return { runs };
    });

  return {
    runs: {},

    start: async (key, input) => {
      if (get().runs[key]?.phase === 'running') return;
      put(key, { phase: 'running', step: 'reading' });
      try {
        let job = await startPhotoAnalysis(input);
        if (!get().runs[key]) return;                  // reset meanwhile
        if (job.status === 'done' && !job.result) job = await getPhotoAnalysis(job.jobId);
        put(key, { phase: 'running', step: 'cutting' });
        const started = Date.now();
        while (job.status === 'pending' || job.status === 'processing') {
          if (Date.now() - started > GIVE_UP_MS) throw new Error('timeout');
          await new Promise((r) => setTimeout(r, POLL_MS));
          if (!get().runs[key]) return;
          job = await getPhotoAnalysis(job.jobId);
        }
        if (job.status === 'failed' || !job.result) throw new Error(job.error || 'failed');
        put(key, { phase: 'result', step: 'cutting', result: job.result });
      } catch (e: any) {
        if (!get().runs[key]) return;
        put(key, { phase: 'error', step: 'cutting', error: e?.message || 'failed' });
      }
    },

    reset: (key) => put(key, undefined),

    offers: {},
    offer: (key, urls) => set((s) => ({ offers: { ...s.offers, [key]: urls } })),
    takeOffer: (key) => {
      const urls = get().offers[key];
      if (urls) {
        set((s) => {
          const offers = { ...s.offers };
          delete offers[key];
          return { offers };
        });
      }
      return urls;
    },
  };
});
